import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectDemoEndTimestamp,
  selectDemoExcludedBaseAssets,
  selectDemoStartTimestamp,
  selectIsDemo,
} from '../store/slices/demo';
import { useGetDemoPortfolioSeriesQuery } from '../store/services/demoApi';
import { aggregateDemoPortfolio } from '../screens/Overview/Summary/Chart/aggregateDemoPortfolio';

interface AggregatedPortfolio {
  dataPoints: { timestamp: number; value: number }[];
  napDataPoints: { timestamp: number; value: number }[];
  nettoDataPoints: { timestamp: number; value: number }[];
  nettoNapDataPoints: { timestamp: number; value: number }[];
}

interface DemoChartPortfolio {
  /** Aggregated demo portfolio when in demo mode — replaces the real query
   *  output. `undefined` when not in demo mode (or while the series is still
   *  loading). */
  demoPortfolio: AggregatedPortfolio | undefined;
  /** Signature for the animation key so Victory recomputes its domain when
   *  the user changes a Setting (exclusion list or date window). Always a
   *  stable string — `'real'` outside demo. */
  demoSettingsKey: string;
}

/**
 * Demo-mode bridge for the Overview Summary chart: fetches the per-asset
 * series payload and aggregates it client-side so the Settings dropdown
 * (exclusion + date window + start-balance rebase) changes the chart instantly
 * without a backend round-trip. In real-user mode the hook is inert.
 */
const useDemoChartPortfolio = (): DemoChartPortfolio => {
  const isDemo = useSelector(selectIsDemo);
  const demoExcluded = useSelector(selectDemoExcludedBaseAssets);
  const demoStart = useSelector(selectDemoStartTimestamp);
  const demoEnd = useSelector(selectDemoEndTimestamp);
  const { data: demoSeriesResp } = useGetDemoPortfolioSeriesQuery(undefined, {
    skip: !isDemo,
  });

  const demoPortfolio = useMemo<AggregatedPortfolio | undefined>(() => {
    if (!isDemo || !demoSeriesResp?.series) return undefined;
    return aggregateDemoPortfolio(demoSeriesResp.series, {
      excludeBaseAssets: demoExcluded,
      startTimestamp: demoStart,
      endTimestamp: demoEnd,
    });
  }, [isDemo, demoSeriesResp, demoExcluded, demoStart, demoEnd]);

  const demoSettingsKey = useMemo(() => {
    if (!isDemo) return 'real';
    const exclKey = [...demoExcluded].sort().join('|');
    return `demo_${demoStart ?? ''}_${demoEnd ?? ''}_${exclKey}`;
  }, [isDemo, demoExcluded, demoStart, demoEnd]);

  return { demoPortfolio, demoSettingsKey };
};

export default useDemoChartPortfolio;
